import { Stack, Text } from '@atlaskit/primitives';
import {
  type ActionMeta,
  CheckboxSelect,
  components,
  type FormatOptionLabelMeta,
  type MultiValueProps,
  type SelectComponentsConfig,
  type StylesConfig,
  type ValueContainerProps
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Children, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export type TreeSelectOption<T extends string = string> = {
  label: string;
  value: T;
};

export type TreeSelectSection<T extends string = string> = {
  label: string;
  options: TreeSelectOption<T>[];
};

interface SimpleTreeMultiSelectInputProps<T extends string = string> {
  inputId: string;
  sections: TreeSelectSection<T>[];
  value: TreeSelectOption<T>[];
  onChange: (value: TreeSelectOption<T>[]) => void;
  label?: ReactNode;
  placeholder?: string;
  ariaLabel?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  allowCrossSectionSelection?: boolean;
  menuPortalTarget?: HTMLElement | null;
}

type TreeFlatOption = {
  label: string;
  value: string;
  kind: 'all' | 'section' | 'option';
  depth: 0 | 1;
  sectionLabel?: string;
  childOptions?: TreeSelectOption<string>[];
};

const ALL_OPTION_VALUE = '__tree_all__';
const sectionValue = (label: string) => `__tree_section__${label}`;
const CONTROL_RESERVED_WIDTH = 88;
const CHIP_HORIZONTAL_PADDING = 52;
const BADGE_HORIZONTAL_PADDING = 28;
const CHIP_FONT =
  '500 14px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const textMeasureCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;

const measureTextWidth = (text: string) => {
  const context = textMeasureCanvas?.getContext('2d');
  if (!context) {
    return text.length * 8;
  }

  context.font = CHIP_FONT;
  return context.measureText(text).width;
};

const estimateChipWidth = (label: string) => {
  return Math.min(measureTextWidth(label) + CHIP_HORIZONTAL_PADDING, 180);
};

const estimateBadgeWidth = (hiddenCount: number) => {
  return measureTextWidth(`+${hiddenCount}`) + BADGE_HORIZONTAL_PADDING;
};

const getVisibleValueCount = (selectedOptions: TreeFlatOption[], containerWidth: number) => {
  if (selectedOptions.length === 0 || containerWidth <= 0) {
    return selectedOptions.length;
  }

  const availableWidth = Math.max(containerWidth - CONTROL_RESERVED_WIDTH, 0);
  let usedWidth = 0;
  let visibleCount = 0;

  for (let index = 0; index < selectedOptions.length; index += 1) {
    const nextOption = selectedOptions[index];

    if (!nextOption) {
      continue;
    }

    const remainingCount = selectedOptions.length - index - 1;
    const chipWidth = estimateChipWidth(nextOption.label);
    const badgeWidth = remainingCount > 0 ? estimateBadgeWidth(remainingCount) : 0;

    if (usedWidth + chipWidth + badgeWidth > availableWidth) {
      break;
    }

    usedWidth += chipWidth;
    visibleCount += 1;
  }

  return visibleCount;
};

export default function SimpleTreeMultiSelectInput<T extends string = string>({
  inputId,
  sections,
  value,
  onChange,
  label,
  placeholder = 'Select options',
  ariaLabel,
  isClearable = true,
  isDisabled = false,
  allowCrossSectionSelection = true,
  menuPortalTarget
}: SimpleTreeMultiSelectInputProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(sections.map((section) => section.label))
  );
  const selectedValues = useMemo<Set<string>>(
    () => new Set<string>(value.map((option) => option.value)),
    [value]
  );
  const allOptions = useMemo(
    () => sections.flatMap((section) => section.options),
    [sections]
  ) as TreeSelectOption[];
  const optionSectionMap = useMemo(
    () =>
      new Map(
        sections.flatMap((section) =>
          section.options.map((option) => [option.value, section.label] as const)
        )
      ),
    [sections]
  );
  const selectedSectionLabels = useMemo(() => {
    const nextSections = new Set<string>();

    value.forEach((option) => {
      const sectionLabel = optionSectionMap.get(option.value);

      if (sectionLabel) {
        nextSections.add(sectionLabel);
      }
    });

    return nextSections;
  }, [optionSectionMap, value]);
  const activeSectionLabel =
    !allowCrossSectionSelection && selectedSectionLabels.size === 1
      ? Array.from(selectedSectionLabels)[0]
      : undefined;
  const allOptionsSelected =
    allOptions.length > 0 && allOptions.every((option) => selectedValues.has(option.value));

  useEffect(() => {
    setExpandedSections((currentSections) => {
      const nextSections = new Set(currentSections);
      sections.forEach((section) => nextSections.add(section.label));
      return nextSections;
    });
  }, [sections]);

  const flatOptions = useMemo<TreeFlatOption[]>(() => {
    const nextOptions: TreeFlatOption[] = [
      {
        depth: 0,
        kind: 'all',
        label: 'Select all',
        value: ALL_OPTION_VALUE
      }
    ];

    sections.forEach((section) => {
      nextOptions.push({
        depth: 0,
        kind: 'section',
        label: section.label,
        sectionLabel: section.label,
        value: sectionValue(section.label),
        childOptions: section.options
      });

      if (expandedSections.has(section.label)) {
        section.options.forEach((option) => {
          nextOptions.push({
            depth: 1,
            kind: 'option',
            label: option.label,
            sectionLabel: section.label,
            value: option.value
          });
        });
      }
    });

    return nextOptions;
  }, [expandedSections, sections]);

  const selectedOptions = useMemo<TreeFlatOption[]>(
    () =>
      value.map((option) => ({
        depth: 1,
        kind: 'option',
        label: option.label,
        value: option.value
      })),
    [value]
  );
  const visibleValueCount = getVisibleValueCount(selectedOptions, containerWidth);
  const hiddenValueCount = Math.max(selectedOptions.length - visibleValueCount, 0);

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const toggleSection = (sectionLabel: string) => {
    setExpandedSections((currentSections) => {
      const nextSections = new Set(currentSections);

      if (nextSections.has(sectionLabel)) {
        nextSections.delete(sectionLabel);
      } else {
        nextSections.add(sectionLabel);
      }

      return nextSections;
    });
  };

  const toggleAllOptions = () => {
    if (!allowCrossSectionSelection) {
      return;
    }

    onChange(allOptionsSelected ? [] : (allOptions as TreeSelectOption<T>[]));
  };

  const toggleSingleOption = (option: TreeFlatOption) => {
    if (
      !allowCrossSectionSelection &&
      activeSectionLabel &&
      option.sectionLabel &&
      option.sectionLabel !== activeSectionLabel
    ) {
      return;
    }

    if (selectedValues.has(option?.value)) {
      onChange(value.filter((selectedOption) => selectedOption.value !== option.value));
      return;
    }

    onChange([...value, { label: option.label, value: option.value as T }]);
  };

  const toggleSectionOptions = (section: TreeFlatOption) => {
    if (
      !allowCrossSectionSelection &&
      activeSectionLabel &&
      section.sectionLabel &&
      section.sectionLabel !== activeSectionLabel
    ) {
      return;
    }

    const sectionOptions = (section.childOptions ?? []) as TreeSelectOption<T>[];
    const sectionOptionValues = new Set(sectionOptions.map((option) => option.value));
    const isSectionSelected =
      sectionOptions.length > 0 &&
      sectionOptions.every((option) => selectedValues.has(option.value));

    if (isSectionSelected) {
      onChange(value.filter((option) => !sectionOptionValues.has(option.value)));
      return;
    }

    const nextOptions = [...value];
    sectionOptions.forEach((option) => {
      if (!selectedValues.has(option.value)) {
        nextOptions.push(option);
      }
    });
    onChange(nextOptions);
  };

  const handleSelectChange = (
    _nextValue: readonly TreeFlatOption[] | null,
    actionMeta?: ActionMeta<TreeFlatOption>
  ) => {
    if (actionMeta?.action === 'clear') {
      onChange([]);
      return;
    }

    if (
      (actionMeta?.action === 'remove-value' || actionMeta?.action === 'pop-value') &&
      'removedValue' in actionMeta &&
      actionMeta.removedValue.kind === 'option'
    ) {
      onChange(value.filter((option) => option.value !== actionMeta.removedValue.value));
      return;
    }

    const selectedOption = actionMeta?.option;

    if (selectedOption?.kind === 'all') {
      toggleAllOptions();
      return;
    }

    if (selectedOption?.kind === 'section') {
      toggleSectionOptions(selectedOption);
      return;
    }

    if (selectedOption?.kind === 'option') {
      toggleSingleOption(selectedOption);
    }
  };

  const isSectionRestricted =
    !allowCrossSectionSelection && selectedSectionLabels.size > 0 && Boolean(activeSectionLabel);

  const isOptionDisabled = (option: TreeFlatOption) => {
    if (option.kind === 'all') {
      return !allowCrossSectionSelection;
    }

    if (!isSectionRestricted) {
      return false;
    }

    if (option.kind === 'section') {
      return option.sectionLabel !== activeSectionLabel;
    }

    return option.sectionLabel !== activeSectionLabel;
  };

  const formatOptionLabel = (
    option: TreeFlatOption,
    { context }: FormatOptionLabelMeta<TreeFlatOption>
  ) => {
    if (context === 'value') {
      return option.label;
    }

    const isSectionExpanded = option.sectionLabel
      ? expandedSections.has(option.sectionLabel)
      : false;
    const optionDisabled = isOptionDisabled(option);
    const selectedSectionCount =
      option.kind === 'section'
        ? (option.childOptions ?? []).filter((item) => selectedValues.has(item.value)).length
        : 0;

    return (
      <span
        onClick={(event) => {
          if (option.kind === 'section') {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onMouseDown={(event) => {
          if (option.kind === 'section') {
            event.preventDefault();
            event.stopPropagation();
            toggleSectionOptions(option);
          }
        }}
        style={{
          alignItems: 'center',
          color: 'var(--app-text)',
          display: 'flex',
          opacity: optionDisabled ? 0.45 : 1,
          fontWeight: option.kind === 'section' ? 600 : 400,
          gap: token('space.100'),
          minWidth: 0,
          paddingInlineStart: option.depth === 1 ? token('space.400') : 0,
          width: '100%'
        }}
      >
        {option.kind === 'section' ? (
          <button
            type="button"
            aria-label={`${isSectionExpanded ? 'Collapse' : 'Expand'} ${option.label}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleSection(option.label);
            }}
            onMouseUp={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            style={{
              alignItems: 'center',
              background: 'transparent',
              border: 0,
              cursor: optionDisabled ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              height: '18px',
              justifyContent: 'center',
              padding: 0,
              width: '18px'
            }}
          >
            <span
              aria-hidden="true"
              style={{
                border: 'solid var(--app-text)',
                borderWidth: '0 1px 1px 0',
                display: 'inline-block',
                height: '8px',
                transform: isSectionExpanded ? 'rotate(45deg)' : 'rotate(-45deg)',
                width: '8px'
              }}
            />
          </button>
        ) : option.kind === 'all' ? (
          <span aria-hidden="true" style={{ width: '18px' }} />
        ) : null}

        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {option.label}
        </span>

        {option.kind === 'section' && selectedSectionCount > 0 ? (
          <Text size="small" color="color.text.subtle">
            {selectedSectionCount}
          </Text>
        ) : null}
      </span>
    );
  };

  const selectComponents: SelectComponentsConfig<TreeFlatOption, true> = {
    MultiValue: (props: MultiValueProps<TreeFlatOption, true>) => {
      if (props.index >= visibleValueCount) {
        return null;
      }

      return <components.MultiValue {...props} />;
    },
    ValueContainer: (props: ValueContainerProps<TreeFlatOption, true>) => {
      if (selectedOptions.length === 0) {
        return <components.ValueContainer {...props}>{props.children}</components.ValueContainer>;
      }

      const children = Children.toArray(props.children);
      const inputChild = children.at(-1);
      const valueChildren = children.slice(0, -1).slice(0, visibleValueCount);

      return (
        <components.ValueContainer {...props}>
          {valueChildren}
          {hiddenValueCount > 0 ? (
            <span
              style={{
                alignItems: 'center',
                backgroundColor: token('color.background.neutral.subtle'),
                borderRadius: '999px',
                color: token('color.text.subtle'),
                display: 'inline-flex',
                flexShrink: 0,
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1,
                marginInlineEnd: token('space.050'),
                padding: `${token('space.025')} ${token('space.075')}`,
                whiteSpace: 'nowrap'
              }}
            >
              +{hiddenValueCount}
            </span>
          ) : null}
          {inputChild}
        </components.ValueContainer>
      );
    }
  };

  const selectStyles: StylesConfig<TreeFlatOption, true> = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? token('color.border.focused') : base.borderColor,
      boxShadow: state.isFocused ? `0 0 0 1px ${token('color.border.focused')}` : base.boxShadow,
      '&:hover': {
        ...base['&:hover'],
        borderColor: state.isFocused ? token('color.border.focused') : base.borderColor
      }
    }),
    menu: (base) => ({
      ...base,
      border: `1px solid ${token('color.border.input')}`,
      boxShadow: token('elevation.shadow.overlay'),
      marginTop: token('space.050')
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '320px',
      paddingBottom: token('space.075'),
      paddingTop: token('space.075')
    }),
    multiValue: (base) => ({
      ...base,
      flexShrink: 0,
      maxWidth: '150px'
    }),
    valueContainer: (base) => ({
      ...base,
      flexWrap: 'nowrap',
      overflow: 'hidden'
    }),
    placeholder: (base) => ({
      ...base,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? token('color.background.neutral') : 'transparent',
      borderLeft: `2px solid ${state.isFocused ? token('color.border.focused') : 'transparent'}`,
      minHeight: '44px'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  return (
    <Stack space="space.050">
      {label ? (
        <label
          htmlFor={inputId}
          style={{
            color: token('color.text'),
            display: 'inline-block',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          {label}
        </label>
      ) : null}

      <div ref={containerRef} style={{ width: '100%' }}>
        <CheckboxSelect<TreeFlatOption>
          inputId={inputId}
          options={flatOptions}
          value={selectedOptions}
          onChange={handleSelectChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isClearable={isClearable}
          isSearchable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuPortalTarget={menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : null)}
          menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
          components={selectComponents}
          formatOptionLabel={formatOptionLabel}
          getOptionValue={(option) => option.value}
          styles={selectStyles}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          isOptionSelected={(option) => {
            if (option.kind === 'all') {
              return allOptionsSelected;
            }

            if (option.kind === 'section') {
              return (
                (option.childOptions?.length ?? 0) > 0 &&
                (option.childOptions ?? []).every((item) => selectedValues.has(item.value))
              );
            }

            return selectedValues.has(option.value as T);
          }}
          isOptionDisabled={isOptionDisabled}
        />
      </div>
    </Stack>
  );
}
